import { ref, onUnmounted } from 'vue'
import type { TokenData, RefreshTokenConfig } from './types'
import { InactivityTracker } from './inactivityTracker'

const DEFAULT_REFRESH_BEFORE_EXPIRY = 300 // 5 minutos antes
const DEFAULT_CHECK_INTERVAL = 60000 // 1 minuto

/**
 * Composable para gerenciar refresh automático de tokens
 * 
 * Monitora a expiração do token e faz refresh automático antes de expirar.
 * Pode ser usado em qualquer app (Pay, Corp, etc.)
 * 
 * @example
 * ```ts
 * const { startRefreshCycle, stopRefreshCycle, refreshNow } = useTokenRefresh({
 *   refreshUrl: '/api/v2/hr_manager/refresh_token',
 *   onRefresh: (tokens) => authStore.updateTokens(tokens),
 *   onRefreshFailed: () => authStore.logout(),
 *   inactivityTimeout: 1800, // 30 min de inatividade
 *   onInactivityTimeout: () => authStore.logout(),
 * })
 * 
 * // Iniciar monitoramento
 * startRefreshCycle(tokenData)
 * ```
 */
export function useTokenRefresh(config: RefreshTokenConfig) {
  const {
    refreshUrl,
    refreshBeforeExpiry = DEFAULT_REFRESH_BEFORE_EXPIRY,
    checkInterval = DEFAULT_CHECK_INTERVAL,
    onRefresh,
    onRefreshFailed,
    getAdditionalHeaders,
    inactivityTimeout,
    onInactivityTimeout,
  } = config

  const isRefreshing = ref(false)
  const lastRefreshAt = ref<Date | null>(null)
  const currentTokenData = ref<TokenData | null>(null)
  const isUserInactive = ref(false)
  let intervalId: ReturnType<typeof setInterval> | null = null
  let inactivityTracker: InactivityTracker | null = null

  /**
   * Verifica se o token está próximo de expirar
   */
  const isTokenExpiringSoon = (tokenData: TokenData): boolean => {
    const expiresAt = tokenData.expiresAt instanceof Date 
      ? tokenData.expiresAt 
      : new Date(tokenData.expiresAt)
    
    const now = new Date()
    const timeUntilExpiry = (expiresAt.getTime() - now.getTime()) / 1000
    
    return timeUntilExpiry <= refreshBeforeExpiry
  }

  /**
   * Verifica se o token já expirou
   */
  const isTokenExpired = (tokenData: TokenData): boolean => {
    const expiresAt = tokenData.expiresAt instanceof Date 
      ? tokenData.expiresAt 
      : new Date(tokenData.expiresAt)
    
    return expiresAt <= new Date()
  }

  /**
   * Faz a chamada de refresh token
   */
  const doRefresh = async (refreshToken: string): Promise<TokenData> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${refreshToken}`,
      ...getAdditionalHeaders?.(),
    }

    const response = await fetch(refreshUrl, {
      method: 'POST',
      headers,
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error(`Refresh failed: ${response.status}`)
    }

    const data = await response.json()
    
    // Suporta diferentes formatos de resposta
    const tokenResponse = data.data?.data || data.data || data
    
    return {
      accessToken: tokenResponse.access_token || tokenResponse.accessToken,
      refreshToken: tokenResponse.refresh_token || tokenResponse.refreshToken,
      expiresAt: tokenResponse.expires_at 
        ? new Date(tokenResponse.expires_at) 
        : new Date(Date.now() + (tokenResponse.expires_in || 3600) * 1000),
      accountId: tokenResponse.account_id || tokenResponse.accountId || null,
    }
  }

  /**
   * Executa o refresh do token
   */
  const refreshNow = async (): Promise<TokenData | null> => {
    if (isRefreshing.value || !currentTokenData.value?.refreshToken) {
      return null
    }

    isRefreshing.value = true

    try {
      const newTokens = await doRefresh(currentTokenData.value.refreshToken)
      
      currentTokenData.value = newTokens
      lastRefreshAt.value = new Date()
      
      onRefresh?.(newTokens)
      
      return newTokens
    } catch (error) {
      console.error('[TokenRefresh] Refresh failed:', error)
      onRefreshFailed?.(error as Error)
      return null
    } finally {
      isRefreshing.value = false
    }
  }

  /**
   * Verifica e faz refresh se necessário
   */
  const checkAndRefresh = async () => {
    if (!currentTokenData.value || isRefreshing.value) {
      return
    }

    const tokenExpired = isTokenExpired(currentTokenData.value)
    const tokenExpiringSoon = isTokenExpiringSoon(currentTokenData.value)

    // Se usuário está inativo
    if (isUserInactive.value) {
      // Se o token já expirou E está inativo, agora sim desloga
      if (tokenExpired) {
        console.log('[TokenRefresh] Token expired while user inactive - logging out')
        onInactivityTimeout?.()
        return
      }
      // Se não expirou ainda, só não renova
      console.log('[TokenRefresh] User inactive, skipping refresh')
      return
    }

    // Usuário ativo - renova se necessário
    if (tokenExpired) {
      console.log('[TokenRefresh] Token expired, attempting refresh...')
      await refreshNow()
      return
    }

    if (tokenExpiringSoon) {
      console.log('[TokenRefresh] Token expiring soon, refreshing proactively...')
      await refreshNow()
    }
  }

  /**
   * Inicia o ciclo de refresh automático
   */
  const startRefreshCycle = (tokenData: TokenData) => {
    currentTokenData.value = tokenData
    
    // Para qualquer ciclo anterior
    stopRefreshCycle()
    
    // Configura tracker de inatividade se habilitado
    if (inactivityTimeout && inactivityTimeout > 0) {
      inactivityTracker = new InactivityTracker({
        inactivityTimeout,
        onInactive: () => {
          console.log('[TokenRefresh] User became inactive - will stop refreshing')
          isUserInactive.value = true
          // NÃO chama onInactivityTimeout aqui - só para de renovar
        },
        onActive: () => {
          console.log('[TokenRefresh] User became active - resuming refresh')
          isUserInactive.value = false
          // Quando volta a ficar ativo, verifica se precisa renovar
          checkAndRefresh()
        },
      })
      inactivityTracker.start()
    }
    
    // Verifica imediatamente
    checkAndRefresh()
    
    // Inicia verificação periódica
    intervalId = setInterval(checkAndRefresh, checkInterval)
  }

  /**
   * Para o ciclo de refresh
   */
  const stopRefreshCycle = () => {
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
    if (inactivityTracker) {
      inactivityTracker.stop()
      inactivityTracker = null
    }
  }

  /**
   * Atualiza os dados do token (sem iniciar novo ciclo)
   */
  const updateTokenData = (tokenData: TokenData) => {
    currentTokenData.value = tokenData
  }

  // Cleanup ao desmontar
  onUnmounted(() => {
    stopRefreshCycle()
  })

  return {
    isRefreshing,
    lastRefreshAt,
    currentTokenData,
    isUserInactive,
    isTokenExpired,
    isTokenExpiringSoon,
    refreshNow,
    startRefreshCycle,
    stopRefreshCycle,
    updateTokenData,
  }
}

/**
 * Versão standalone (sem Vue lifecycle)
 * Para uso em stores ou fora de componentes
 */
export class TokenRefreshManager {
  private config: RefreshTokenConfig
  private tokenData: TokenData | null = null
  private intervalId: ReturnType<typeof setInterval> | null = null
  private isRefreshing = false
  private inactivityTracker: InactivityTracker | null = null
  private isUserInactive = false

  constructor(config: RefreshTokenConfig) {
    this.config = {
      refreshBeforeExpiry: DEFAULT_REFRESH_BEFORE_EXPIRY,
      checkInterval: DEFAULT_CHECK_INTERVAL,
      ...config,
    }
  }

  private isTokenExpiringSoon(): boolean {
    if (!this.tokenData) return false
    
    const expiresAt = this.tokenData.expiresAt instanceof Date 
      ? this.tokenData.expiresAt 
      : new Date(this.tokenData.expiresAt)
    
    const now = new Date()
    const timeUntilExpiry = (expiresAt.getTime() - now.getTime()) / 1000
    
    return timeUntilExpiry <= this.config.refreshBeforeExpiry!
  }

  private isTokenExpired(): boolean {
    if (!this.tokenData) return true
    
    const expiresAt = this.tokenData.expiresAt instanceof Date 
      ? this.tokenData.expiresAt 
      : new Date(this.tokenData.expiresAt)
    
    return expiresAt <= new Date()
  }

  private async doRefresh(): Promise<TokenData> {
    if (!this.tokenData?.refreshToken) {
      throw new Error('No refresh token available')
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...this.config.getAdditionalHeaders?.(),
    }

    // Envia refresh_token no body conforme documentação da API
    const response = await fetch(this.config.refreshUrl, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        refresh_token: this.tokenData.refreshToken,
      }),
    })

    if (!response.ok) {
      throw new Error(`Refresh failed: ${response.status}`)
    }

    const data = await response.json()
    const tokenResponse = data.data?.data || data.data || data
    
    return {
      accessToken: tokenResponse.access_token || tokenResponse.accessToken,
      refreshToken: tokenResponse.refresh_token || tokenResponse.refreshToken,
      expiresAt: tokenResponse.expires_at 
        ? new Date(tokenResponse.expires_at) 
        : new Date(Date.now() + (tokenResponse.expires_in || 3600) * 1000),
      accountId: tokenResponse.account_id || tokenResponse.accountId || null,
    }
  }

  /**
   * @param skipFailureHandler - quando true, não chama onRefreshFailed (ex.: hidratação no router decide redirect)
   */
  async refresh(skipFailureHandler = false): Promise<TokenData | null> {
    if (this.isRefreshing) return null

    this.isRefreshing = true

    try {
      const newTokens = await this.doRefresh()
      this.tokenData = newTokens
      this.config.onRefresh?.(newTokens)
      return newTokens
    } catch (error) {
      console.error('[TokenRefreshManager] Refresh failed:', error)
      if (!skipFailureHandler) {
        this.config.onRefreshFailed?.(error as Error)
      }
      return null
    } finally {
      this.isRefreshing = false
    }
  }

  private async checkAndRefresh() {
    if (this.isRefreshing) return

    const tokenExpired = this.isTokenExpired()
    const tokenExpiringSoon = this.isTokenExpiringSoon()

    // Se usuário está inativo
    if (this.isUserInactive) {
      // Se o token já expirou E está inativo, agora sim desloga
      if (tokenExpired) {
        console.log('[TokenRefreshManager] Token expired while user inactive - logging out')
        this.config.onInactivityTimeout?.()
        return
      }
      // Se não expirou ainda, só não renova
      console.log('[TokenRefreshManager] User inactive, skipping refresh')
      return
    }

    // Usuário ativo - renova se necessário
    if (tokenExpired || tokenExpiringSoon) {
      await this.refresh()
    }
  }

  start(tokenData: TokenData) {
    this.tokenData = tokenData
    this.stop()
    
    // Configura tracker de inatividade se habilitado
    if (this.config.inactivityTimeout && this.config.inactivityTimeout > 0) {
      this.inactivityTracker = new InactivityTracker({
        inactivityTimeout: this.config.inactivityTimeout,
        onInactive: () => {
          console.log('[TokenRefreshManager] User became inactive - will stop refreshing')
          this.isUserInactive = true
          // NÃO chama onInactivityTimeout aqui - só para de renovar
          // O logout só acontece quando o token expirar E o usuário estiver inativo
        },
        onActive: () => {
          console.log('[TokenRefreshManager] User became active - resuming refresh')
          this.isUserInactive = false
          // Quando volta a ficar ativo, verifica se precisa renovar
          this.checkAndRefresh()
        },
      })
      this.inactivityTracker.start()
    }
    
    // Verifica imediatamente
    this.checkAndRefresh()
    
    // Inicia verificação periódica
    this.intervalId = setInterval(
      () => this.checkAndRefresh(), 
      this.config.checkInterval!
    )
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
    if (this.inactivityTracker) {
      this.inactivityTracker.stop()
      this.inactivityTracker = null
    }
  }

  updateTokens(tokenData: TokenData) {
    this.tokenData = tokenData
  }

  getTokenData(): TokenData | null {
    return this.tokenData
  }

  getIsRefreshing(): boolean {
    return this.isRefreshing
  }

  getIsUserInactive(): boolean {
    return this.isUserInactive
  }
}
